import { fetchExperienceCategories } from 'actions/ExperienceActions'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Typography from 'LuxKit/Typography/Typography'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import ExperienceYAMLList from '../ExperienceLists/ExperienceYAMLList'
import Group from 'components/utils/Group'

const Separator = styled.div`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const NoTicketsSection = styled(Pane)`
  > * + * {
    margin-top: ${rem(40)};
  }
`

interface Props {
  experience: App.ExperienceOffer
}

function ExperienceOfferPageUnavailableTickets(props: Props) {
  const { experience } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const categories = useAppSelector(state => state.experience.experienceCategories)
  const [search] = useExperienceSearchUrl({
    latLong: experience.location,
    categoryIds: experience.primaryCategory?.parentId ? [experience.primaryCategory.parentId] : [],
  })

  const parentCategory = useMemo(() => categories?.find(cat => cat.id === experience.primaryCategory?.parentId), [categories, experience])

  return <NoTicketsSection border="neutral-five" borderRadius="M" padding={40}>
    <Typography align="center">
      <VerticalSpacer gap={24}>
        <VerticalSpacer gap={8}>
          <Heading variant="heading6">{intl.formatMessage({
            id: 'Xh5pe57kt9XwtftohSwn',
            defaultMessage: 'No tickets available',
            description: 'Heading when no tickets are available for sold out experience',
          })}
          </Heading>
          <BodyText variant="large">
            {intl.formatMessage({
              id: 'wyeQBquj4vCkNZ5R2ncj',
              defaultMessage: 'Sorry, this offer is sold out. Browse our other featured experiences or check out other similar offers below.',
              description: 'Description text when experience tickets are unavailable',
            })}
          </BodyText>
        </VerticalSpacer>
        <TextButton kind="primary" to="/experiences">{intl.formatMessage({
          id: '46YagMyFSZC8TSa4u9Vy',
          defaultMessage: 'Browse all experiences',
          description: 'Button text to browse all experiences when tickets are unavailable',
        })}
        </TextButton>
      </VerticalSpacer>
    </Typography>
    <Separator />
    <VerticalSpacer gap={24}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Heading variant="heading3">{intl.formatMessage(
          {
            id: 'fc4ZVEtD1Yc7UtfNXdJs',
            defaultMessage: 'Other {categoryName} offers',
            description: 'Heading for other offers in the same category when tickets are unavailable',
          },
          { categoryName: parentCategory?.name },
        )}
        </Heading>
        <TextButton kind="tertiary" horizontalOutdent="end" to={search}>
          {intl.formatMessage({
            id: '8c2ZjX6ozwALt1OJtCH-',
            defaultMessage: 'View all',
            description: 'Link to view all offers in category when tickets are unavailable',
          })}
        </TextButton>
      </Group>
      <ExperienceYAMLList
        experience={experience}
        pageSize={3}
        gutterSize={40}
      />
    </VerticalSpacer>
  </NoTicketsSection>
}

export default ExperienceOfferPageUnavailableTickets
